import React, { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import { API_URL } from '../../config';

export default function LedgerView({ adminData }) {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchLedger();
  }, [period]);

  const fetchLedger = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/ledger?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch ledger');
      
      const data = await res.json();
      setLedger(data.ledger || []);
    } catch (error) {
      console.error('Error fetching ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'payment': return 'text-[#10B981]';
      case 'admin_action': return 'text-orange-500';
      case 'snap_transfer': return 'text-blue-500';
      default: return 'text-white/70';
    }
  };

  return (
    <div className="p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-full mx-auto w-full">
        
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Ledger <span className="text-[#10B981]">(Unveränderbar)</span>
            </h2>
            <p className="text-white/60 text-sm">
              Komplettes Journal aller Transaktionen, Admin-Aktionen und Transfers
            </p>
          </div>
          
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full md:w-auto px-4 py-2 bg-black border-2 border-white/30 rounded-xl text-white"
          >
            <option value="today">Heute</option>
            <option value="7d">7 Tage</option>
            <option value="30d">30 Tage</option>
            <option value="month">Monat</option>
            <option value="year">Jahr</option>
          </select>
        </div>

        {/* Ledger Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-2">
            {ledger.map((entry, index) => (
              <div key={index} className="p-4 bg-black border border-white/10 rounded-xl hover:border-white/20 transition-all">
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <FileText className={`w-5 h-5 ${getTypeColor(entry.type)}`} />
                    <div className="flex-1">
                      <p className="font-medium text-white">{entry.description}</p>
                      <p className="text-xs text-white/50">
                        {new Date(entry.timestamp).toLocaleString('de-DE')} · User: {entry.user_id.substring(0, 12)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {entry.amount_eur && (
                      <p className="font-bold text-white">€{entry.amount_eur.toFixed(2)}</p>
                    )}
                    {entry.snaps_change && (
                      <p className={`text-sm font-bold ${entry.snaps_change > 0 ? 'text-[#10B981]' : 'text-orange-500'}`}>
                        {entry.snaps_change > 0 ? '+' : ''}{entry.snaps_change} SNAPs
                      </p>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${getTypeColor(entry.type)} bg-white/5`}>
                      {entry.type}
                    </span>
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${getTypeColor(entry.type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white break-words">{entry.description}</p>
                        <p className="text-xs text-white/50 mt-1">
                          {new Date(entry.timestamp).toLocaleString('de-DE')}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          User: {entry.user_id.substring(0, 12)}
                        </p>
                      </div>
                    </div>
                    
                    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap flex-shrink-0 ${getTypeColor(entry.type)} bg-white/5`}>
                      {entry.type}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    {entry.amount_eur && (
                      <div>
                        <div className="text-xs text-white/50 mb-1">Betrag</div>
                        <div className="font-bold text-white">€{entry.amount_eur.toFixed(2)}</div>
                      </div>
                    )}
                    {entry.snaps_change && (
                      <div className="text-right">
                        <div className="text-xs text-white/50 mb-1">SNAPs</div>
                        <div className={`text-sm font-bold ${entry.snaps_change > 0 ? 'text-[#10B981]' : 'text-orange-500'}`}>
                          {entry.snaps_change > 0 ? '+' : ''}{entry.snaps_change}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {entry.reference && (
                  <p className="text-xs text-white/40 mt-2 break-all">Ref: {entry.reference}</p>
                )}
              </div>
            ))}
            
            {ledger.length === 0 && (
              <div className="text-center py-12 text-white/50">
                Keine Einträge gefunden
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
